import { type FC, useState } from "react";
import type { QrCode } from "../types";
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import QrCodeDisplayModal from "./QrCodeDisplayModal";
import QRCode from 'qrcode';
import jsPDF from "jspdf";
import TemplateUploader, { Coords } from "./TemplateUploader";
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

// --- Icons ---
const Icons = {
    Close: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>,
    Pdf: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>,
    Zip: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>,
    Template: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
    Download: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>,
    Qr: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v1m6 11h2m-6 0h-2v4h2v-4zM6 8V4h6v4H6zm14 10a2 2 0 11-4 0 2 2 0 014 0zM6 20h6v-4H6v4z" /></svg>
};

interface Props {
    qrCodes: QrCode[];
    onClose: () => void;
    download: boolean;
    onRemove?: (id: number) => void;
    title?: string;
    subtitle?: string;
}

const ITEMS_PER_PAGE = 8; // Slightly reduced for better spacing
const BATCH_SIZE = 350;

const QrCodeListDisplayModal: FC<Props> = ({ qrCodes, onClose, download, onRemove, title, subtitle }) => {
    const [page, setPage] = useState(1);
    const [selectedQrCode, setSelectedQrCode] = useState<QrCode | null>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);

    // Progress State
    const [generationProgress, setGenerationProgress] = useState({
        active: false,
        percentage: 0,
        current: 0,
        total: 0,
        currentBatch: 0,
        totalBatches: 0
    });

    // Template State
    const [templateFile, setTemplateFile] = useState<File | null>(null);
    const [templateCoords, setTemplateCoords] = useState<Coords | null>(null);

    const totalPages = Math.ceil(qrCodes.length / ITEMS_PER_PAGE);
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    const currentItems = qrCodes.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    const handleNextPage = () => setPage(prev => Math.min(prev + 1, totalPages));
    const handlePrevPage = () => setPage(prev => Math.max(prev - 1, 1));

    const handleTemplateDefined = (file: File, coords: Coords) => {
        setTemplateFile(file);
        setTemplateCoords(coords);
        setIsTemplateModalOpen(false);
    };

    // --- LOGIC: Generate from Template ---
    const handleGenerateFromTemplate = async () => {
        if (!templateFile || !templateCoords || !PDFDocument || !JSZip) return;

        const totalBatches = Math.ceil(qrCodes.length / BATCH_SIZE);
        setSubmitError(null);
        setGenerationProgress({ active: true, percentage: 0, current: 0, total: qrCodes.length, currentBatch: 1, totalBatches });

        try {
            const templatePdfBytes = await templateFile.arrayBuffer();

            for (let i = 0; i < qrCodes.length; i += BATCH_SIZE) {
                const batch = qrCodes.slice(i, i + BATCH_SIZE);
                const zip = new JSZip();
                const currentBatchNum = (i / BATCH_SIZE) + 1;

                setGenerationProgress(prev => ({ ...prev, currentBatch: currentBatchNum }));

                for (let j = 0; j < batch.length; j++) {
                    const qr = batch[j];
                    const pdfDoc = await PDFDocument.load(templatePdfBytes);
                    const page = pdfDoc.getPages()[0];
                    const qrDataUrl = await QRCode.toDataURL(qr.code, { type: 'image/png', errorCorrectionLevel: 'H' });
                    const qrImage = await pdfDoc.embedPng(qrDataUrl);
                    const { x, y, width, height } = templateCoords;
                    page.drawImage(qrImage, { x, y: page.getHeight() - y - height, width, height });

                    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
                    const { width: pageWidth, height: pageHeight } = page.getSize();

                    const idText = String(qr.id);
                    const textSize = 12;
                    const textWidth = font.widthOfTextAtSize(idText, textSize);
                    const margin = 50;

                    page.drawText(idText, {
                        x: pageWidth - textWidth - margin,
                        y: pageHeight - margin,
                        size: textSize,
                        font: font,
                        color: rgb(0, 0, 0),
                    });

                    const pdfBytes = await pdfDoc.save();
                    zip.file(`${qr.name.replace(/\s+/g, '_').toLowerCase()}.pdf`, pdfBytes);

                    const overallProgress = i + j + 1;
                    const percentage = Math.round((overallProgress / qrCodes.length) * 100);

                    await new Promise(resolve => {
                        setGenerationProgress(prev => ({ ...prev, current: overallProgress, percentage }));
                        setTimeout(resolve, 0);
                    });
                }

                const content = await zip.generateAsync({ type: "blob" });
                saveAs(content, `qrcodes-parte-${currentBatchNum}-di-${totalBatches}.zip`);
            }
            await new Promise(resolve => setTimeout(resolve, 1500));
        } catch (error: any) {
            console.error("Errore nella generazione da modello:", error);
            setSubmitError(error.message || "Si è verificato un errore.");
        } finally {
            setGenerationProgress({ active: false, percentage: 0, current: 0, total: 0, currentBatch: 0, totalBatches: 0 });
        }
    };

    // --- LOGIC: Download PDF Grid ---
    const handleDownloadAll = async () => {
        setSubmitError(null);

        try {
            const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            const qrSize = 35;
            const margin = 13;
            const cols = 5;
            const rows = 6;
            const pageWidth = doc.internal.pageSize.getWidth();
            let x = margin;
            let y = margin;
            let qrCount = 0;

            for (let i = 0; i < qrCodes.length; i++) {
                const qr = qrCodes[i];
                const tempCanvas = document.createElement('canvas');
                await QRCode.toCanvas(tempCanvas, qr.code, {
                    width: qrSize * 3.78,
                    errorCorrectionLevel: 'H',
                });

                const imgData = tempCanvas.toDataURL('image/jpeg', 1.0);
                doc.addImage(imgData, 'JPEG', x, y, qrSize, qrSize);
                doc.setFontSize(10);
                doc.text(qr.id.toString(), x + qrSize / 2, y + qrSize + 5, { align: 'center' });

                qrCount++;
                x += (pageWidth - 2 * margin) / cols;

                if (qrCount % cols === 0) {
                    x = margin;
                    y += qrSize + 15;
                }

                if (qrCount % (cols * rows) === 0 && qrCount < qrCodes.length) {
                    doc.addPage();
                    x = margin;
                    y = margin;
                }
                const newCurrent = i + 1;
                const newPercentage = Math.round((newCurrent / qrCodes.length) * 100);
                setGenerationProgress(prev => ({ ...prev, active: true, current: newCurrent, percentage: newPercentage, currentBatch: 1, totalBatches: 1 }));
            }

            doc.save("qr-codes.pdf");
            setGenerationProgress(prev => ({ ...prev, active: false, current: 0, percentage: 0, currentBatch: 0, totalBatches: 0 }));

        } catch (error: any) {
            console.error("Errore nella generazione del PDF:", error);
            setSubmitError(error.message || "Si è verificato un errore durante la generazione del file PDF.");
            setGenerationProgress(prev => ({ ...prev, active: false }));
        }
    };

    // --- LOGIC: Download Images ZIP ---
    const handleDownloadAllZip = async () => {
        setSubmitError(null);
        const zip = new JSZip();

        try {
            for (let i = 0; i < qrCodes.length; i++) {
                const qr = qrCodes[i];
                const size = 256;
                const padding = 20;
                const newSize = size + 2 * padding;

                const tempCanvas = document.createElement('canvas');
                tempCanvas.width = newSize;
                tempCanvas.height = newSize;

                const context = tempCanvas.getContext('2d');
                if (!context) throw new Error("Impossibile ottenere il contesto 2D");
                context.fillStyle = '#FFFFFF';
                context.fillRect(0, 0, newSize, newSize);
                context.translate(padding, padding);

                await QRCode.toCanvas(tempCanvas, qr.code, { width: size, margin: 5, errorCorrectionLevel: qr.category.toLowerCase() === 'catering' ? 'L' : 'H' });

                const blob = await new Promise<Blob | null>((resolve) => tempCanvas.toBlob(resolve, 'image/png'));
                if (blob) zip.file(`${qr.id}_${qr.name.replace(/\s+/g, '_').toLowerCase()}.png`, blob);

                const newCurrent = i + 1;
                const newPercentage = Math.round((newCurrent / qrCodes.length) * 100);
                setGenerationProgress(prev => ({ ...prev, active: true, current: newCurrent, percentage: newPercentage, currentBatch: 1, totalBatches: 1 }));
            }

            const content = await zip.generateAsync({ type: "blob" });
            saveAs(content, "qr-codes.zip");
            setGenerationProgress(prev => ({ ...prev, active: false, current: 0, percentage: 0, currentBatch: 0, totalBatches: 0 }));

        } catch (error: any) {
            console.error("Errore nella generazione del file ZIP:", error);
            setSubmitError(error.message || "Si è verificato un errore durante la generazione del file ZIP.");
            setGenerationProgress(prev => ({ ...prev, active: false }));
        }
    };


    return (
        <>
            {/* MAIN MODAL */}
            <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex justify-center items-center p-4 z-50 animate-fade-in">
                <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-3xl shadow-2xl flex flex-col max-h-[90vh]">

                    {/* Header */}
                    <div className="p-6 border-b border-slate-700/50 flex justify-between items-center bg-slate-900/50 rounded-t-2xl">
                        <div>
                            <h2 className="text-xl font-bold text-white flex items-center gap-2">
                                <span className="p-2 bg-sky-500/20 rounded-lg text-sky-400">{Icons.Qr}</span>
                                {title ?? "Lista QR Code Generati"}
                            </h2>
                            <p className="text-sm text-slate-400 mt-1 ml-11">
                                {subtitle ?? `${qrCodes.length} QR Code pronti per l'uso`}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-slate-400 hover:text-white p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
                        >
                            {Icons.Close}
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
                        {generationProgress.active ? (
                            /* LOADING STATE */
                            <div className="flex flex-col items-center justify-center h-full min-h-[300px] animate-in fade-in">
                                <div className="relative w-24 h-24 mb-6">
                                    <svg className="animate-spin w-full h-full text-sky-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                </div>
                                <h3 className="text-2xl font-bold text-white mb-2">Generazione in corso...</h3>
                                <p className="text-slate-400 mb-6 text-center max-w-md">
                                    Stiamo preparando i tuoi file. L'operazione potrebbe richiedere qualche secondo in base al numero di QR.
                                </p>

                                <div className="w-full max-w-md bg-slate-700 rounded-full h-4 mb-3 overflow-hidden">
                                    <div
                                        className="bg-gradient-to-r from-sky-500 to-indigo-500 h-full rounded-full transition-all duration-300"
                                        style={{ width: `${generationProgress.percentage}%` }}
                                    ></div>
                                </div>
                                <div className="flex justify-between w-full max-w-md text-xs text-slate-400 font-mono">
                                    <span>File: {generationProgress.current} / {qrCodes.length}</span>
                                    <span>{generationProgress.percentage}%</span>
                                </div>
                            </div>
                        ) : (
                            /* NORMAL STATE */
                            <div className="space-y-6">
                                {/* List Grid */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {currentItems.map((qr) => (
                                        <div key={qr.id} className="bg-slate-700/30 border border-slate-700/50 p-3 rounded-xl flex justify-between items-center hover:bg-slate-700/50 transition-colors group">
                                            <div className="flex-1 min-w-0 mr-3">
                                                <h4 className="font-semibold text-white truncate text-sm">{qr.name}</h4>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <span className="text-xs px-2 py-0.5 rounded bg-slate-800 text-slate-400 border border-slate-700">
                                                        #{qr.id}
                                                    </span>
                                                    <span className="text-xs text-slate-500 truncate">{qr.category}</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => setSelectedQrCode(qr)}
                                                    className="px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-800 text-sky-400 border border-slate-700 hover:bg-sky-500 hover:text-white hover:border-sky-500 transition-all"
                                                >
                                                    Dettagli
                                                </button>
                                                {onRemove && (
                                                    <button
                                                        onClick={() => onRemove(qr.id)}
                                                        title="Rimuovi dalla selezione"
                                                        className="p-1.5 rounded-lg bg-slate-800 text-rose-400 border border-slate-700 hover:bg-rose-500 hover:text-white hover:border-rose-500 transition-all"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="flex justify-center items-center gap-4 py-2">
                                        <button
                                            onClick={handlePrevPage}
                                            disabled={page === 1}
                                            className="px-4 py-2 rounded-lg text-sm font-medium text-slate-300 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            Precedente
                                        </button>
                                        <span className="text-sm font-medium text-white bg-slate-700/50 px-3 py-1 rounded-md">
                                            {page} / {totalPages}
                                        </span>
                                        <button
                                            onClick={handleNextPage}
                                            disabled={page === totalPages}
                                            className="px-4 py-2 rounded-lg text-sm font-medium text-slate-300 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            Successiva
                                        </button>
                                    </div>
                                )}

                                {/* Download Actions Section */}
                                {download && (
                                    <div className="pt-6 border-t border-slate-700/50 grid grid-cols-1 md:grid-cols-2 gap-6">

                                        {/* Simple Download Card */}
                                        <div className="bg-slate-700/20 border border-slate-700/50 rounded-xl p-5 hover:bg-slate-700/30 transition-colors">
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="p-2 bg-emerald-500/10 rounded-lg text-emerald-400">
                                                    {Icons.Download}
                                                </div>
                                                <h3 className="text-base font-bold text-white">Download Rapido</h3>
                                            </div>
                                            <div className="space-y-3">
                                                <button
                                                    onClick={handleDownloadAll}
                                                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-500 transition-colors"
                                                >
                                                    {Icons.Pdf} Scarica Griglia PDF
                                                </button>
                                                <button
                                                    onClick={handleDownloadAllZip}
                                                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-slate-700 hover:bg-slate-600 border border-slate-600 transition-colors"
                                                >
                                                    {Icons.Zip} Scarica Immagini ZIP
                                                </button>
                                            </div>
                                        </div>

                                        {/* Template Download Card */}
                                        <div className="bg-slate-700/20 border border-slate-700/50 rounded-xl p-5 hover:bg-slate-700/30 transition-colors">
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="p-2 bg-indigo-500/10 rounded-lg text-indigo-400">
                                                    {Icons.Template}
                                                </div>
                                                <h3 className="text-base font-bold text-white">Download Personalizzato</h3>
                                            </div>
                                            <div className="space-y-3">
                                                <button
                                                    onClick={() => setIsTemplateModalOpen(true)}
                                                    className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors ${templateFile ? 'bg-indigo-600 hover:bg-indigo-500' : 'bg-slate-700 hover:bg-slate-600 border border-slate-600'}`}
                                                >
                                                    {templateFile ? 'Modifica Modello' : 'Carica Modello PDF'}
                                                </button>

                                                <button
                                                    onClick={handleGenerateFromTemplate}
                                                    disabled={!templateFile}
                                                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-teal-600 hover:bg-teal-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-slate-700 transition-colors"
                                                >
                                                    {Icons.Zip} Genera con Modello
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {submitError && (
                                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm text-center">
                                        {submitError}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* TEMPLATE MODAL OVERLAY */}
            {isTemplateModalOpen && (
                <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex justify-center items-center p-4 z-[60] animate-fade-in">
                    <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 max-w-5xl w-full h-[90vh] flex flex-col">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-white">Editor Modello PDF</h2>
                            <button
                                onClick={() => setIsTemplateModalOpen(false)}
                                className="px-4 py-2 text-sm font-medium rounded-lg bg-slate-700 text-white hover:bg-slate-600 transition-colors"
                            >
                                Chiudi Editor
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto custom-scrollbar bg-slate-900/50 rounded-xl border border-slate-700/50 p-2 relative">
                            <TemplateUploader onTemplateDefined={handleTemplateDefined} />
                        </div>
                    </div>
                </div>
            )}

            {/* DETAIL MODAL OVERLAY */}
            {selectedQrCode && (
                <QrCodeDisplayModal qrCode={selectedQrCode} onClose={() => setSelectedQrCode(null)} />
            )}
        </>
    );
};

export default QrCodeListDisplayModal;